import type { RelayClient } from '@polymarket/builder-relayer-client';

import type { TradingWalletKind } from '@/types/auth.types';

export type RelayMetaTx = {
  to: string;
  data: `0x${string}` | string;
  value?: string;
};

/**
 * Executes a batch of meta-transactions through either:
 * - Gnosis Safe relayer
 * - Deposit Wallet relayer
 *
 * IMPORTANT:
 * For deposit_wallet, `executionAddress` must be the user's deposit wallet address.
 * For gnosis_safe, it should be the Safe address.
 */
export async function executeRelayMetaTxBatch(
  client: RelayClient,
  executionAddress: string,
  tradingWalletKind: TradingWalletKind | undefined,
  transactions: RelayMetaTx[],
  metadata: string
) {
  const kind = tradingWalletKind ?? 'gnosis_safe';

  if (kind === 'deposit_wallet') {
    if (typeof (client as any).executeDepositWalletBatch !== 'function') {
      throw new Error(
        'Deposit wallet relay calls require @polymarket/builder-relayer-client >= 0.0.9'
      );
    }

    const calls = transactions.map((txn) => ({
      target: txn.to,
      value: txn.value ?? '0',
      data: txn.data as `0x${string}`,
    }));

    // 10 minute expiry
    const deadline = Math.floor(Date.now() / 1000 + 600).toString();

    // executionAddress MUST be depositWalletAddress
    try {
      return await (client as any).executeDepositWalletBatch(calls, executionAddress, deadline);
    } catch (error: any) {
      // The relayer client can reject with a plain object ({error, status, data})
      // rather than an Error — check data.error and the serialized form too.
      const msg =
        typeof error === 'string'
          ? error
          : error?.message || error?.data?.error || JSON.stringify(error ?? '');

      // Auto-heal missing Relayer registry entries
      if (msg.includes('wallet registry validation failed')) {
        console.warn(
          `[executeRelayMetaTxBatch] Relayer registry missing wallet ${executionAddress}. Attempting to force-register...`
        );
        if (typeof (client as any).deployDepositWallet === 'function') {
          try {
            const deployRes = await (client as any).deployDepositWallet();
            // Wait until the WALLET-CREATE actually mines — retrying the batch
            // before the relayer registers the wallet just fails again. Note:
            // wait()/pollUntilState returns `undefined` on failure, not a throw.
            const mined =
              typeof deployRes?.wait === 'function' ? await deployRes.wait() : deployRes;
            if (!mined) {
              throw new Error('Force-registration transaction failed or timed out on the relayer');
            }
          } catch (regErr: any) {
            const regMsg =
              typeof regErr === 'string' ? regErr : (regErr?.message ?? String(regErr));
            // "already deployed" means the registry should catch up — still retry
            // the batch below. Anything else is a real failure worth surfacing.
            if (!/already|exists/i.test(regMsg)) {
              console.error('[executeRelayMetaTxBatch] Force-registration failed:', regErr);
              throw error; // rethrow the original batch error
            }
          }
          console.info('[executeRelayMetaTxBatch] Retrying batch after force-registration...');
          return await (client as any).executeDepositWalletBatch(calls, executionAddress, deadline);
        }
      }
      throw error;
    }
  }

  // Gnosis Safe path
  return (client as any).executeSafeTransactions(
    transactions.map((txn) => ({
      to: txn.to as `0x${string}`,
      data: txn.data as `0x${string}`,
      value: txn.value ?? '0',
      operation: 0,
    })),
    metadata
  );
}
